/* Size chart dialog — native <dialog> met unit-toggle en variant-sync.
   De maatadviseur zit in assets/size-finder.js. Eén custom element, geen dependencies. */

if (!customElements.get('size-chart-dialog')) {
  const UNIT_KEY = 'dt:size-unit';

  const storage = {
    get(key) {
      try { return localStorage.getItem(key); } catch (e) { return null; }
    },
    set(key, value) {
      try { localStorage.setItem(key, value); } catch (e) { /* private mode */ }
    }
  };

  const formatNl = (num) => {
    let str = Math.round(num * 10) / 10 + '';
    return str.replace('.', ',');
  };

  const pushDataLayer = (data) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
  };

  // Gedeelde maatlabel-matching (ook gebruikt door size-finder.js).
  // Store-data mixt systemen: "2XL" vs "XXL", "2 XL", composieten als "34/XS"
  // en chartcellen als "36/38" — normaliseren + splitsen maakt ze vergelijkbaar.
  window.themeSizeMatch = window.themeSizeMatch || (() => {
    const FAMILY = { XXL: '2XL', XXXL: '3XL', XXXXL: '4XL', XXXXXL: '5XL' };
    const normalize = (value) => {
      const s = String(value == null ? '' : value).trim().toUpperCase().replace(/\s+/g, '');
      return FAMILY[s] || s;
    };
    const aliases = (value) => {
      const raw = String(value == null ? '' : value).trim();
      if (!raw) return [];
      // Haakjes-toelichtingen ("46 (38 Engelse maat)", "S (35-38)"): het deel
      // ervóór en maat-achtige tokens erbínnen tellen allebei als alias.
      const base = raw.replace(/\([^)]*\)/g, ' ').trim();
      const innerTokens = (raw.match(/\(([^)]*)\)/g) || [])
        .flatMap((group) => group.slice(1, -1).split(/[\s/]+/))
        .filter((token) => /^\d{1,3}([.,]5)?(-\d{1,3}([.,]5)?)?$/.test(token) || /^\d?X{0,4}[SML]{1,3}$/i.test(token));
      const parts = base.split('/').concat(innerTokens).map(normalize).filter(Boolean);
      const self = normalize(raw);
      if (self && !parts.includes(self)) parts.push(self);
      // Bereik-labels ("54-56", "M-L") dekken beide maten — helften ook als alias
      parts.slice().forEach((part) => {
        const range = part.match(/^([A-Z0-9]+)-([A-Z0-9]+)$/);
        if (range) [range[1], range[2]].forEach((half) => {
          if (!parts.includes(half)) parts.push(half);
        });
      });
      return parts;
    };
    const matches = (a, b) => {
      const setA = Array.isArray(a) ? a : aliases(a);
      const setB = Array.isArray(b) ? b : aliases(b);
      return setA.some((x) => setB.includes(x));
    };
    return { normalize, aliases, matches };
  })();

  class SizeChartDialog extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      this.opener = null;

      this.dialog.addEventListener('click', this.handleDialogClick.bind(this));
      this.dialog.addEventListener('close', () => {
        if (this.opener && document.contains(this.opener)) this.opener.focus();
      });

      document.addEventListener('on:variant:change', (evt) => {
        const sid = evt.target && evt.target.dataset ? evt.target.dataset.sectionId : null;
        if (sid && sid !== this.sectionId) return;
        this.highlightSelected(evt.detail && evt.detail.selectedOptions);
      });

      this.initUnitToggle();
      this.highlightSelected();
    }

    open(opener) {
      this.opener = opener || null;
      this.highlightSelected();
      this.dialog.showModal();
      const finder = this.querySelector('size-finder');
      pushDataLayer({
        event: 'size_chart_open',
        sizeChart: finder ? finder.dataset.chartHandle || '' : '',
        hasFinder: !!(finder && !finder.hidden),
        hasAdvice: !!(opener && opener.classList && opener.classList.contains('size-chart-trigger--advice'))
      });
    }

    close() {
      this.dialog.close();
    }

    handleDialogClick(evt) {
      if (evt.target.closest('.js-close-size-chart')) {
        this.close();
        return;
      }

      // Backdrop-klik: alleen als er direct op het <dialog>-element is geklikt
      if (evt.target === this.dialog) {
        const rect = this.dialog.getBoundingClientRect();
        const inside = evt.clientX >= rect.left && evt.clientX <= rect.right
          && evt.clientY >= rect.top && evt.clientY <= rect.bottom;
        if (!inside) this.close();
        return;
      }

      const unitBtn = evt.target.closest('[data-unit]');
      if (unitBtn) {
        this.setUnit(unitBtn.dataset.unit);
        return;
      }

      const row = evt.target.closest('tbody tr[data-size-labels]');
      if (row && !evt.target.closest('a, button, input, label')) {
        this.selectVariantForRow(row);
      }
    }

    /* ---- Variant-sync ---- */

    getPicker() {
      return document.querySelector(`variant-picker[data-section-id="${this.sectionId}"]`);
    }

    getSelectedOptionValues() {
      const picker = this.getPicker();
      if (!picker) return [];
      return Array.from(picker.querySelectorAll('.js-option:checked')).map((input) => input.value);
    }

    highlightSelected(selectedOptions) {
      const match = window.themeSizeMatch;
      const valueAliases = (selectedOptions || this.getSelectedOptionValues())
        .filter(Boolean)
        .flatMap((v) => match.aliases(v));
      this.querySelectorAll('tbody tr[data-size-labels]').forEach((row) => {
        const rowAliases = row.dataset.sizeLabels.split('|').filter(Boolean).flatMap((l) => match.aliases(l));
        row.classList.toggle('is-selected', match.matches(rowAliases, valueAliases));
      });
    }

    selectVariantForRow(row) {
      const match = window.themeSizeMatch;
      const picker = this.getPicker();
      if (!picker) return false;
      const rowAliases = row.dataset.sizeLabels.split('|').filter(Boolean).flatMap((l) => match.aliases(l));
      const inputs = Array.from(picker.querySelectorAll('.js-option'));
      const candidates = inputs.filter((input) => match.matches(match.aliases(input.value), rowAliases));
      const target = candidates.find((input) => !input.classList.contains('is-unavailable')) || candidates[0];
      if (!target || target.checked) return !!target;
      target.checked = true;
      target.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    /* ---- cm/inch toggle ---- */

    initUnitToggle() {
      this.unitButtons = this.querySelectorAll('[data-unit]');
      if (!this.unitButtons.length) return;
      const saved = storage.get(UNIT_KEY);
      if (saved === 'in') this.setUnit('in');
    }

    setUnit(unit) {
      this.querySelectorAll('[data-cm]').forEach((cell) => {
        if (unit === 'in' && !cell.dataset.in) {
          const parts = cell.dataset.cm.split('-').map(parseFloat);
          cell.dataset.in = parts.map((cm) => formatNl(cm / 2.54)).join('–');
          cell.dataset.cmText = cell.textContent;
        }
        cell.textContent = unit === 'in' ? cell.dataset.in : (cell.dataset.cmText || cell.textContent);
      });
      this.unitButtons.forEach((btn) => {
        const active = btn.dataset.unit === unit;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      storage.set(UNIT_KEY, unit);
    }

  }

  customElements.define('size-chart-dialog', SizeChartDialog);

  // Triggers: document-brede delegatie zodat DOM-swaps van de variant picker
  // (section rendering) de knop niet breken. Triggers zonder dialog op de
  // pagina (bv. in de quick-buy drawer) worden verborgen.
  document.addEventListener('click', (evt) => {
    const trigger = evt.target.closest('[data-size-chart-open]');
    if (!trigger) return;
    const dialog = document.getElementById(trigger.dataset.sizeChartOpen);
    const component = dialog && dialog.closest('size-chart-dialog');
    if (component) {
      component.open(trigger);
    } else {
      trigger.hidden = true;
    }
  });

  const hideOrphanTriggers = (root) => {
    (root || document).querySelectorAll('[data-size-chart-open]').forEach((trigger) => {
      if (!document.getElementById(trigger.dataset.sizeChartOpen)) trigger.hidden = true;
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => hideOrphanTriggers());
  } else {
    hideOrphanTriggers();
  }
  document.addEventListener('on:quickbuy:after-open', () => hideOrphanTriggers());
}
